import { Card, Row, Col } from "components/Base/Containers";
import { Box, Typography, useTheme } from "@mui/material";
import { ArrowDropDown, Close, Search } from "@mui/icons-material";
import { useTooltips } from "hooks/useTooltips";
import { TooltipTrigger } from "components/Tooltip/styled";
import { ReactComponent as HelpIcon } from "assets/icons/warning/circle_help.svg";
import { DefinitionTooltip } from "components/Tooltip";
import { useEffect, useRef, useState } from "react";
import {
  DNA_CLASS_CONFIG_MAP,
  DNA_ATTRIBUTE_CONFIG_MAP,
  DNAAttribute,
  DNAClass,
  SCORE_LEVEL,
  MAX_TRAIT_SCORE,
} from "pages/cabal-id/dna/dnaConfig";
import { FilterByClassModal } from "./modals/FilterByClassModal";
import { FilterByTraitModal } from "./modals/FilterByTraitModal";
import { CabalFreeGroupRowV2 } from "components/CabalGroup";
import useUserStoreV2 from "store/user-store-v2/useUserStoreV2";
import { useInfiniteQuery } from "@tanstack/react-query";
import axiosService from "services/axios";
import { useInViewInfiniteQuery } from "hooks/useInViewInfiniteQuery";
import { useLocation } from "react-router-dom";
import { Cabal } from "types";
import { Scrollable } from "components/Base/Scrollable";
import { BaseButton } from "components/Base/Buttons";
import { SearchDrawer } from "components/SearchDrawer";
import { BaseSelect } from "components/Base/Select";

export const DEFAULT_TRAITS: [DNAAttribute, number[]][] = [
  ["accuracyScore", [0, 5]],
  ["pumpScore", [0, 5]],
  ["socialScore", [0, 5]],
  ["earlyScore", [0, 5]],
  ["insightScore", [0, 5]],
];

const searchFilterDrawerConfig = {
  key: "groupSearchFilter",
  placeholder: "Search group",
};

const languageSelectOptions = [
  { label: "Languages", value: "all" },
  {
    label: "English",
    value: "en",
  },
  {
    label: "한국어",
    value: "ko",
  },
  {
    label: "中文",
    value: "zh",
  },
  {
    label: "русский",
    value: "ru",
  },
  {
    label: "Tiếng Việt",
    value: "vi",
  },
];

export const GroupPage = () => {
  const theme = useTheme();

  /* Search Drawer */
  const [isOpened, setIsOpened] = useState(false);

  /* User */
  const { tgUserId, walletAddresses = {} } = useUserStoreV2();
  const { tonAddress } = walletAddresses;
  const [page, setPage] = useState(1);

  /* Filter Modal */
  const [isShowFilterByClassModal, setIsShowFilterByClassModal] = useState(false);
  const [isShowFilterByTraitModal, setIsShowFilterByTraitModal] = useState(false);

  /* Filters */
  const [selectedClasses, setSelectedClasses] = useState<Map<DNAClass, boolean>>(
    localStorage.getItem("selectedClasses")
      ? new Map(JSON.parse(localStorage.getItem("selectedClasses")!))
      : new Map(),
  );
  const [selectedTraits, setSelectedTraits] = useState<Map<DNAAttribute, number[]>>(
    localStorage.getItem("selectedTraits")
      ? new Map(JSON.parse(localStorage.getItem("selectedTraits")!))
      : new Map(DEFAULT_TRAITS),
  );
  const [selectedLanguage, setSelectedLanguage] = useState(
    localStorage.getItem("selectedLanguage")
      ? localStorage.getItem("selectedLanguage")
      : languageSelectOptions[0].value,
  );

  /* Search Filter */
  const [searchFilter, setSearchFilter] = useState(
    localStorage.getItem(searchFilterDrawerConfig.key),
  );

  /* Data */
  const queryResult = useInfiniteQuery({
    queryKey: [
      "tg-groups",
      {
        page,
        language: selectedLanguage ?? "all",
        name: "",
        groupType: "all",
        pricing: "all",
        mode: "all",
        ...(selectedClasses.size > 0
          ? { classNames: Array.from(selectedClasses.keys()).join(",") }
          : {}),
        ...(selectedTraits.size > 0
          ? Array.from(selectedTraits.keys()).reduce((acc, key) => {
              const value = selectedTraits.get(key as DNAAttribute);
              if (value) {
                const min =
                  value[0] === 5
                    ? MAX_TRAIT_SCORE
                    : SCORE_LEVEL[
                        ["VERY_LOW", "LOW", "MEDIUM", "HIGH", "VERY_HIGH"][
                          value[0]
                        ] as keyof typeof SCORE_LEVEL
                      ]!.toString();
                const max =
                  value[1] === 5
                    ? MAX_TRAIT_SCORE
                    : SCORE_LEVEL[
                        ["VERY_LOW", "LOW", "MEDIUM", "HIGH", "VERY_HIGH"][
                          value[1]
                        ] as keyof typeof SCORE_LEVEL
                      ]!.toString();
                acc[key] = `${min}:${max}`;
              }
              return acc;
            }, {} as Record<DNAAttribute, string>)
          : {}),
      },
    ],
    queryFn: async ({ pageParam }) => {
      return axiosService.getTelegramGroups({
        filter: {
          pricing: "all",
          language: selectedLanguage ?? "all",
          groupType: "all",
          name: "",
          tgUserId: tgUserId?.toString(),
          tonAddress,
          ...(selectedClasses.size > 0
            ? { classNames: Array.from(selectedClasses.keys()).join(",") }
            : {}),
          ...(selectedTraits.size > 0
            ? Array.from(selectedTraits.keys()).reduce((acc, key) => {
                const value = selectedTraits.get(key as DNAAttribute);
                if (value) {
                  const min =
                    value[0] === 5
                      ? MAX_TRAIT_SCORE
                      : SCORE_LEVEL[
                          ["VERY_LOW", "LOW", "MEDIUM", "HIGH", "VERY_HIGH"][
                            value[0]
                          ] as keyof typeof SCORE_LEVEL
                        ]!.toString();
                  const max =
                    value[1] === 5
                      ? MAX_TRAIT_SCORE
                      : SCORE_LEVEL[
                          ["VERY_LOW", "LOW", "MEDIUM", "HIGH", "VERY_HIGH"][
                            value[1]
                          ] as keyof typeof SCORE_LEVEL
                        ]!.toString();
                  acc[key] = `${min}:${max}`;
                }
                return acc;
              }, {} as Record<DNAAttribute, string>)
            : {}),
        },
        selects: [],
        limit: 10,
        page: pageParam,
      });
    },
    getNextPageParam: (lastPage, pages) => {
      return lastPage.page + 1;
    },

    getPreviousPageParam: (firstPage, pages) => {
      return firstPage.page - 1;
    },
    initialPageParam: 1,
  });
  const { data, isLoading, isSuccess } = queryResult;

  const [pageBySearch, setPageBySearch] = useState(1);
  const queryResultBySearch = useInfiniteQuery({
    queryKey: [
      "tg-groups-search",
      {
        page: pageBySearch,
        language: selectedLanguage ?? "all",
        name: "",
        groupType: "all",
        pricing: "all",
        mode: "all",
        ...(searchFilter ? { search: searchFilter } : {}),
        ...Array.from(selectedTraits.keys()).reduce((acc, key) => {
          const min = 0;
          const max = MAX_TRAIT_SCORE;
          acc[key] = `${min}:${max}`;

          return acc;
        }, {} as Record<DNAAttribute, string>),
      },
    ],
    queryFn: async ({ pageParam }) => {
      return axiosService.getTelegramGroups({
        filter: {
          pricing: "all",
          language: selectedLanguage ?? "all",
          groupType: "all",
          name: "",
          tgUserId: tgUserId?.toString(),
          tonAddress,
          ...(searchFilter ? { search: searchFilter } : {}),
          ...Array.from(selectedTraits.keys()).reduce((acc, key) => {
            const min = 0;
            const max = MAX_TRAIT_SCORE;
            acc[key] = `${min}:${max}`;

            return acc;
          }, {} as Record<DNAAttribute, string>),
        },
        selects: [],
        limit: 10,
        page: pageParam,
      });
    },
    getNextPageParam: (lastPage, pages) => {
      return lastPage.page + 1;
    },

    getPreviousPageParam: (firstPage, pages) => {
      return firstPage.page - 1;
    },
    initialPageParam: 1,
  });
  const {
    data: dataBySearch,
    isLoading: isLoadingBySearch,
    isSuccess: isSuccessBySearch,
  } = queryResultBySearch;

  const { ref, hasAllFetched } = useInViewInfiniteQuery(queryResult);
  const { ref: refBySearch, hasAllFetched: hasAllFetchedBySearch } =
    useInViewInfiniteQuery(queryResultBySearch);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const isShowBuyNowModal = search ? !!searchParams.get("isShowListNowModal") : false;

  /* Handlers */
  const handleFilterByClassModalOpen = () => {
    if (selectedClasses.size > 0) {
      setSelectedClasses(new Map());
      localStorage.removeItem("selectedClasses");
    } else {
      setIsShowFilterByClassModal(true);
    }
  };

  const handleFilterByTraitModalOpen = () => {
    if ([...selectedTraits.values()]?.some((value) => value[0] !== 0 || value[1] !== 5)) {
      setSelectedTraits(new Map(DEFAULT_TRAITS));
      localStorage.removeItem("selectedTraits");
    } else {
      setIsShowFilterByTraitModal(true);
    }
  };

  const handleCabalClick = () => {
    if (scrollableRef.current) {
      localStorage.setItem("leaderboardHeight", scrollableRef.current.scrollTop.toString());
    }
  };

  useEffect(() => {
    if (window.localStorage.getItem("leaderboardHeight") !== undefined) {
      const scrollHeight = window.localStorage.getItem("leaderboardHeight");
      if (scrollHeight) {
        window.localStorage.removeItem("leaderboardHeight");
        scrollableRef.current?.scrollTo(0, scrollHeight ? parseInt(scrollHeight) : 0);
      }
    }
  }, [data]);

  return (
    <>
      <Col sx={{ alignItems: "start", justifyContent: "start", gap: "8px" }} fullWidth fullHeight>
        <Header
          // title="DISCOVER TOP SHILLERS"
          title="DISCOVER SMART COMMUNITY"
          tooltipText="Top communities based on SHILL score - a measurement of influence that is awarded through
          a blend of attention, alpha quality and effort"
        />
        <Row sx={{ justifyContent: "space-between" }} fullWidth>
          <Row
            section="filters"
            sx={{ gap: "4px", justifyContent: "start", alignItems: "center", flexWrap: "wrap" }}
            fullWidth>
            <FilterItem
              title="Classes"
              selectedUI={<ClassFilterSelectedUI selectedClasses={selectedClasses} />}
              isSelected={!selectedClasses.size}
              onClick={handleFilterByClassModalOpen}
            />
            <FilterItem
              title="Traits"
              selectedUI={<TraitFilterSelectedUI selectedTraits={selectedTraits} />}
              isSelected={[...selectedTraits.values()]?.every(
                (value) => value[0] === 0 && value[1] === 5,
              )}
              onClick={handleFilterByTraitModalOpen}
            />
            <BaseSelect
              defaultValue={languageSelectOptions[0].value}
              value={selectedLanguage}
              onChange={(event) => {
                localStorage.setItem("selectedLanguage", event.target.value as string);
                setSelectedLanguage(event.target.value as string);
              }}
              options={languageSelectOptions}
            />
          </Row>
          <Row sx={{ justifyContent: "end", gap: "10px" }}>
            <BaseButton variant="primary" onClick={() => setIsOpened(true)}>
              <Row sx={{ padding: "4px 8px", gap: "4px" }}>
                <Search />
              </Row>
            </BaseButton>
          </Row>
        </Row>
        <Row sx={{ alignItems: "start", maxHeight: "100%" }} fullWidth fullHeight>
          <Scrollable
            scrollRef={scrollableRef}
            isLoading={isLoading}
            isEmpty={data?.pages[0].totalResults === 0}
            hasAllFetched={hasAllFetched}
            loadMoreRef={ref}>
            {data?.pages
              .flatMap((page) => page.results)
              .map((group: any, index: number) => {
                // if (!group.masterAddress) {
                return (
                  <Box sx={{ width: "100%" }} key={group._id}>
                    <CabalFreeGroupRowV2
                      cabal={group as Cabal}
                      rank={index + 1}
                      onClick={handleCabalClick}
                    />
                  </Box>
                );
              })}
          </Scrollable>
        </Row>
      </Col>
      <FilterByClassModal
        isOpen={isShowFilterByClassModal}
        close={() => setIsShowFilterByClassModal(false)}
        selectedClasses={selectedClasses}
        setSelectedClasses={setSelectedClasses}
      />
      <FilterByTraitModal
        isOpen={isShowFilterByTraitModal}
        close={() => setIsShowFilterByTraitModal(false)}
        selectedTraits={selectedTraits}
        setSelectedTraits={setSelectedTraits}
      />
      <SearchDrawer
        isOpenned={isOpened}
        close={() => setIsOpened(false)}
        data={dataBySearch?.pages.flatMap((page) => page.results) || []}
        isLoadedData={isSuccessBySearch}
        isLoadingData={isLoadingBySearch}
        cachedKey={searchFilterDrawerConfig.key}
        placeholder={searchFilterDrawerConfig.placeholder}
        onSearch={(search) => {
          setSearchFilter(search);
        }}>
        <Row sx={{ alignItems: "start", marginTop: "9px", maxHeight: "100%" }} fullWidth fullHeight>
          <Scrollable
            isLoading={isLoadingBySearch}
            isEmpty={dataBySearch?.pages[0].totalResults === 0}
            hasAllFetched={hasAllFetchedBySearch}
            loadMoreRef={refBySearch}>
            {dataBySearch?.pages
              .flatMap((page) => page.results)
              .map((group: any, index: number) => {
                // if (!group.masterAddress) {
                return (
                  <Box sx={{ width: "100%" }} key={group._id}>
                    <CabalFreeGroupRowV2
                      cabal={group as Cabal}
                      rank={index + 4}
                      onClick={handleCabalClick}
                    />
                  </Box>
                );
              })}
          </Scrollable>
        </Row>
      </SearchDrawer>
    </>
  );
};

const FilterItem = ({
  title,
  selectedUI,
  isSelected,
  onClick,
}: {
  title: string;
  selectedUI: React.ReactNode;
  isSelected: boolean;
  onClick: () => void;
}) => {
  const theme = useTheme();
  return (
    <Col sx={{ cursor: "pointer" }} onClick={onClick}>
      <Card._2D
        type="row"
        sx={{ borderRadius: theme.spacing(1.5), padding: theme.spacing(0.5, 2) }}>
        {isSelected ? (
          <Row>
            <Col>
              <Typography variant="body-default" color="text.secondary">
                {title}
              </Typography>
            </Col>
            <Col>
              <ArrowDropDown />
            </Col>
          </Row>
        ) : (
          selectedUI
        )}
      </Card._2D>
    </Col>
  );
};

const ClassFilterSelectedUI = ({
  selectedClasses,
}: {
  selectedClasses: Map<DNAClass, boolean>;
}) => {
  return (
    <Row sx={{ padding: "2px 0px", gap: "4px" }}>
      <Col>
        <Typography variant="body-default" sx={{ whiteSpace: "nowrap" }}>
          {`${DNA_CLASS_CONFIG_MAP[Array.from(selectedClasses.keys())[0]]?.name} ${
            selectedClasses.size > 1 ? `+${selectedClasses.size - 1}` : ""
          }`}
        </Typography>
      </Col>
      <Row>
        <Close sx={{ width: "16px", height: "16px" }} />
      </Row>
    </Row>
  );
};

const TraitFilterSelectedUI = ({
  selectedTraits,
}: {
  selectedTraits: Map<DNAAttribute, number[]>;
}) => {
  return (
    <Row sx={{ padding: "2px 0px", gap: "4px" }}>
      <Col>
        <Typography variant="body-default">
          {`${
            DNA_ATTRIBUTE_CONFIG_MAP[
              Array.from(selectedTraits.entries()).filter(
                ([key, value]) => value[0] !== 0 || value[1] !== 5,
              )[0][0]
            ]?.name
          } ${
            [...selectedTraits.values()]?.filter((value) => value[0] !== 0 || value[1] !== 5)
              .length > 1
              ? `+${
                  [...selectedTraits.values()]?.filter((value) => value[0] !== 0 || value[1] !== 5)
                    .length - 1
                }`
              : ""
          }`}
        </Typography>
      </Col>
      <Row>
        <Close sx={{ width: "16px", height: "16px" }} />
      </Row>
    </Row>
  );
};
const Header = ({ title, tooltipText }: { title: string; tooltipText: string }) => {
  const { isTooltipOpen, setIsTooltipOpen, tooltipRef } = useTooltips();

  const handleTriggerClick = () => {
    setIsTooltipOpen(!isTooltipOpen);
  };

  return (
    <>
      {/* Header */}
      <Row sx={{ gap: "4px", whiteSpace: "nowrap" }}>
        <Typography variant="title-h3">{title}</Typography>
        <TooltipTrigger ref={tooltipRef} isOpen={isTooltipOpen} onClick={handleTriggerClick}>
          <Row position={"absolute"} top={-14} left={"50%"}>
            <HelpIcon width={"26px"} height={"26px"} />
          </Row>
        </TooltipTrigger>
      </Row>
      {/* Tooltip Modal */}
      <DefinitionTooltip term={title} isShow={isTooltipOpen} maxWidth="480px">
        <Typography variant="body-default">{tooltipText}</Typography>
      </DefinitionTooltip>
    </>
  );
};
